import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Fade } from "react-reveal";
import Header from "../../components/header/Header";
import TopButton from "../../components/topButton/TopButton";
import { LanguageContext } from "../../i18n";
import { darajatContent } from "./darajatContent";
import "./DarajatPage.css";

export default function DarajatPage({ theme, product }) {
  const { language } = useContext(LanguageContext);
  const copy = darajatContent[language] || darajatContent.en;
  const [selected, setSelected] = useState(0);
  const dialog = useRef(null);
  const opener = useRef(null);
  const shot = copy.shots[selected];
  const shotSizes = {
    "statistics.png": [1915, 992],
    "results.png": [1907, 921],
    "printing.png": [1191, 840],
    "import-export.png": [1911, 914],
  };
  const sections = [
    "darajat-features",
    "darajat-gallery",
    "darajat-requirements",
    "darajat-faq",
  ];
  const support =
    "https://wa.me/9647713380002?text=" +
    encodeURIComponent(
      language === "ar"
        ? "مرحبًا، أود الاستفسار عن تفعيل برنامج درجات."
        : "Hello, I would like to ask about activating Darajat."
    );

  useEffect(() => {
    if (!window.location.hash) window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  function showImage() {
    dialog.current.showModal();
  }

  const download = (
    <a
      className="darajat-button darajat-primary"
      href={product.downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <path d="M12 3v12m-5-5 5 5 5-5M4 15v5h16v-5" />
      </svg>
      {copy.download}
    </a>
  );

  return (
    <div className="darajat-page">
      <Header theme={theme} />
      <Helmet>
        <title>
          {copy.name} | {copy.title}
        </title>
        <meta name="description" content={copy.intro} />
        <meta property="og:title" content={copy.name + " — " + copy.title} />
        <meta property="og:description" content={copy.intro} />
        <meta property="og:url" content="https://samr.work/products/darajat" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://samr.work/media/darajat/results.png"
        />
        <link rel="canonical" href="https://samr.work/products/darajat" />
      </Helmet>
      <main className="darajat-container">
        <Link className="darajat-back" to="/home#products">
          {copy.back}
        </Link>
        <section className="darajat-hero">
          <div className="darajat-intro">
            <div className="darajat-brand">
              <img src={product.logo} alt="" width="76" height="76" />
              <div>
                <span className="darajat-eyebrow">{copy.eyebrow}</span>
                <p>
                  {copy.name}
                  <span className="darajat-version">2.0</span>
                </p>
              </div>
            </div>
            <h1>{copy.title}</h1>
            <p className="darajat-lead">{copy.intro}</p>
            <div className="darajat-actions">
              {download}
              <a
                className="darajat-button darajat-whatsapp"
                href={support}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="iconify"
                  data-icon="fa-brands:whatsapp"
                  aria-hidden="true"
                />
                {copy.support}
              </a>
              <a
                className="darajat-button darajat-secondary"
                href="#darajat-gallery"
              >
                {copy.explore}
              </a>
            </div>
            <p className="darajat-trial">{copy.trial}</p>
          </div>
          <a
            className="darajat-hero-preview"
            href="#darajat-gallery"
            aria-label={copy.explore}
          >
            <div className="darajat-window-bar">
              <span aria-hidden="true">● ● ●</span>
              <span>Darajat / {copy.shots[0][1]}</span>
            </div>
            <img
              src="/media/darajat/statistics.png"
              width="1915"
              height="992"
              alt={copy.shots[0][2]}
            />
            <span className="darajat-preview-caption">{copy.shots[0][3]}</span>
          </a>
        </section>

        <ul className="darajat-facts">
          {copy.facts.map((fact) => (
            <li key={fact}>
              <span aria-hidden="true">✓</span>
              {fact}
            </li>
          ))}
        </ul>
        <nav className="darajat-section-nav" aria-label={copy.name}>
          {copy.nav.map((title, i) => (
            <a key={sections[i]} href={"#" + sections[i]}>
              {title}
            </a>
          ))}
        </nav>

        <section className="darajat-section" id="darajat-features">
          <span className="darajat-eyebrow">01 / {copy.nav[0]}</span>
          <h2>{copy.benefitsTitle}</h2>
          <p className="darajat-section-intro">{copy.benefitsIntro}</p>
          <div className="darajat-feature-grid">
            {copy.features.map(([icon, title, text]) => (
              <Fade bottom duration={500} distance="16px" key={icon}>
                <article className="darajat-feature">
                  <span
                    className="iconify"
                    data-icon={icon}
                    aria-hidden="true"
                  />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              </Fade>
            ))}
          </div>
        </section>

        <section
          className="darajat-statistics"
          aria-labelledby="darajat-statistics-title"
        >
          <div>
            <span
              className="iconify"
              data-icon="mdi:chart-box-outline"
              aria-hidden="true"
            />
            <h2 id="darajat-statistics-title">{copy.statisticsTitle}</h2>
            <p>{copy.statisticsIntro}</p>
            <a
              className="darajat-button darajat-secondary"
              href="#darajat-gallery"
              onClick={() => setSelected(0)}
            >
              {copy.statisticsLink}
            </a>
          </div>
          <ul>
            {copy.statisticsPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>

        <section className="darajat-section" id="darajat-gallery">
          <span className="darajat-eyebrow">02 / {copy.nav[1]}</span>
          <h2>{copy.galleryTitle}</h2>
          <p className="darajat-section-intro">{copy.galleryIntro}</p>
          <div className="darajat-gallery-controls">
            {copy.shots.map((item, i) => (
              <button
                key={item[0]}
                type="button"
                aria-pressed={selected === i}
                onClick={() => setSelected(i)}
              >
                {item[1]}
              </button>
            ))}
          </div>
          <figure className="darajat-gallery">
            <button
              type="button"
              className="darajat-image-button"
              ref={opener}
              onClick={showImage}
              aria-label={copy.zoom}
            >
              <img
                src={"/media/darajat/" + shot[0]}
                alt={shot[2]}
                width={shotSizes[shot[0]][0]}
                height={shotSizes[shot[0]][1]}
                loading="lazy"
              />
              <span className="darajat-enlarge" aria-hidden="true">
                ↗ {copy.zoom}
              </span>
            </button>
            <figcaption>
              <h3>{shot[2]}</h3>
              <p>{shot[3]}</p>
            </figcaption>
          </figure>
        </section>

        <section className="darajat-school-band">
          <div>
            <h2>{copy.stagesTitle}</h2>
            <p>{copy.stagesIntro}</p>
          </div>
          <ul>
            {copy.stages.map((stage) => (
              <li key={stage}>{stage}</li>
            ))}
          </ul>
        </section>

        <section className="darajat-section">
          <h2>{copy.workflowTitle}</h2>
          <ol className="darajat-steps">
            {copy.steps.map(([title, text], i) => (
              <li key={i}>
                <span className="darajat-step-number" aria-hidden="true">
                  0{i + 1}
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="darajat-section" id="darajat-requirements">
          <span className="darajat-eyebrow">03 / {copy.nav[2]}</span>
          <h2>{copy.requirementsTitle}</h2>
          <p className="darajat-section-intro">{copy.requirementsIntro}</p>
          <div className="darajat-requirements-layout">
            <dl className="darajat-requirements">
              {copy.requirements.map(([title, text]) => (
                <div key={title}>
                  <dt>{title}</dt>
                  <dd>{text}</dd>
                </div>
              ))}
            </dl>
            <aside className="darajat-privacy">
              <span
                className="iconify"
                data-icon="mdi:database-outline"
                aria-hidden="true"
              />
              <h3>{copy.privacyTitle}</h3>
              <p>{copy.privacyText}</p>
            </aside>
          </div>
        </section>

        <section className="darajat-section darajat-faq" id="darajat-faq">
          <div>
            <span className="darajat-eyebrow">04 / {copy.nav[3]}</span>
            <h2>{copy.faqTitle}</h2>
          </div>
          <div>
            {copy.faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="darajat-download" id="darajat-download">
          <img
            src={product.logo}
            alt=""
            width="80"
            height="80"
            loading="lazy"
          />
          <h2>{copy.ctaTitle}</h2>
          <p>{copy.ctaText}</p>
          <div className="darajat-actions">
            {download}
            <a
              className="darajat-button darajat-whatsapp"
              href={support}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span
                className="iconify"
                data-icon="fa-brands:whatsapp"
                aria-hidden="true"
              />
              {copy.support}
            </a>
          </div>
          <p className="darajat-trial">{copy.trial}</p>
          <small>{copy.release}</small>
        </section>
      </main>
      <dialog
        className="darajat-lightbox"
        ref={dialog}
        aria-label={copy.zoom}
        onClose={() => opener.current && opener.current.focus()}
        onClick={(event) => {
          if (event.target === event.currentTarget) dialog.current.close();
        }}
      >
        <button
          className="darajat-lightbox-close"
          type="button"
          onClick={() => dialog.current.close()}
          autoFocus
        >
          {copy.close} ×
        </button>
        <img src={"/media/darajat/" + shot[0]} alt={shot[2]} />
      </dialog>
      <TopButton theme={theme} />
    </div>
  );
}
